import React from 'react'

import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { AppBar, Toolbar } from '@mui/material'
import { makeStyles } from '@mui/styles'

import AccountButton from '~/src/Account/AccountButton'
import MembershipChooser from '~/src/Facility/MembershipChooser'
import createLogger from '~/src/Lib/Logging'
import { idAndNameShape } from '~/src/Lib/PropTypes'
import { EMPTY_OBJECT } from '~/src/Lib/Utils'
import Button from '~/src/UI/Shared/Button'
import { APP_BAR_HEIGHT } from '~/src/UI/Theme/main'

import { PUBLIC_ROUTES } from '../Routes/constants'

const displayName = 'AppHeader'
const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  appBar: {
    paddingBottom: theme.spacing(0.3),
    gridArea: 'appBar',
    pointerEvents: 'none',
    height: APP_BAR_HEIGHT,
    backgroundColor: theme.palette.background.default,
    boxShadow: 'none',
    transition: theme.transitions.create('filter', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '& button, a, input': {
      pointerEvents: 'auto',
    },
    '& [class*=MuiToolbar-root]': {
      minHeight: APP_BAR_HEIGHT,
      maxHeight: APP_BAR_HEIGHT,
      justifyContent: 'flex-end',
      paddingLeft: theme.spacing(3),
      '@media print': {
        padding: 0
      }
    },
    paddingRight: theme.spacing(5),
    '@media print': {
      padding: 0,
    },
  },
  toolbar: {
    display: 'grid',
    containerType: 'size',
    gridTemplateAreas: [
      '"title selector account"',
    ].join('\n'),
    gridTemplateColumns: 'minmax(0, 1fr) auto auto',
    [theme.breakpoints.down('sm')]: {
      gridTemplateAreas: [
        '"selector account"',
        '"title title"',
      ].join('\n'),
      gridTemplateColumns: '1fr auto',
    },
    '@media print': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }
  },
  pageTitle: {
    gridArea: 'title',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
  },
  facilitySwitcher: {
    gridArea: 'selector',
    marginRight: 0,
    marginLeft: 0,
    alignSelf: 'center',
    width: '14rem',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing()
    },
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(2.5)
    },
    '@media print': {
      width: '33vw',
      marginRight: 0,
      '& [class*=MuiAutocomplete-inputRoot]': {
        paddingRight: '0 !important',
      },
      '& input': {
        textAlign: 'end',
      }
    }
  },
  accountButton: {
    gridArea: 'account',
    maxWidth: '33vw',
    '@media print': {
      display: 'none'
    }
  },
}), { name: displayName })

/**
 * @param {Object} props - Component props.
 * @param {Object} props.currentMembership
 * @param {Object} props.facilities
 * @param {string} props.facilityName
 * @param {Object[]} props.memberships
 * @param {Function} props.spinnerOn
 * @returns { JSX.Element }
 */
const AppHeader = ({ currentMembership = EMPTY_OBJECT, doAuthReset }) => {
  const classes = useStyles()
  return (
    <AppBar
      aria-labelledby="pageTitle"
      className={classes.appBar}
      color="inherit"
      data-trackheight="appBar"
      gutter="true"
      position="static"
    >
      <Toolbar disableGutters className={classes.toolbar}>
        <div id="pageTitle" className={classes.pageTitle} />
        <div className={classes.facilitySwitcher}>
          <MembershipChooser />
        </div>
        <div className={classes.accountButton}>
          {currentMembership?.id
            ? <AccountButton />
            : (
              <Button
                href={PUBLIC_ROUTES.LOGIN}
                onClick={doAuthReset}
              >
                Login again
              </Button>
            )}
        </div>
      </Toolbar>
    </AppBar>
  )
}
AppHeader.propTypes = {
  currentMembership: idAndNameShape,
  doAuthReset: PropTypes.func.isRequired,
}

const Connected = props => {
  const { currentMembership, doAuthReset } = useConnect('selectCurrentMembership', 'doAuthReset')
  return (
    <AppHeader
      {...props}
      currentMembership={currentMembership}
      doAuthReset={doAuthReset}
    />
  )
}

Connected.displayName = `Connected(${AppHeader.displayName})`

export default Connected
